import App from "../views/App.tsx";
import { createBrowserRouter, redirect } from "react-router-dom";
import ErrorPage from "../views/ErrorPage.tsx";
import { authRoutes } from "./authRoutes.tsx";
import { privateRoutes } from "./privateRoutes.tsx";
import { sxUiRoutes } from "./sxUiRoutes.tsx";
import { IS_PROD } from "../services/config.ts";
import ContactPage from "../views/contact/ContactPage.tsx";
import { userService } from "../services/user/userService.ts";
import { authService } from "../services/auth/authService.ts";
import CapsulePublicPage from "../views/organization/capsule/CapsulePublicPage.tsx";
import {
  getCapsuleByOrganizationSlugAndId,
  getCapsuleByUidRequest,
} from "../services/capsule/capsuleApi.ts";
import { Capsule } from "../services/capsule/capsuleModel.ts";
import { loggerBuilder } from "../services/logger.ts";
import { toastsWithIntl } from "../services/toastService.tsx";
import SharedPage from "../views/organization/shared/SharedPage.tsx";
import {
  getEmbedProfileByUidRequest,
  getProfilesByUidRequest,
} from "../services/profile/profileApi.ts";
import { Profile } from "../services/profile/profileModel.ts";
import { getOrganizationRequest } from "../services/organization/organizationApi.ts";
import { fetchRetriable } from "../react-helpers/fetch.ts";
import { useLayoutEffect } from "react";
import CapsuleEmbedPage from "../views/embed/CapsuleEmbedPage.tsx";
import ProfileEmbedPage from "../views/embed/ProfileEmbedPage.tsx";
import EmbedErrorPage from "../views/embed/ErrorPage.tsx";
import PremiumSponsorshipConsentPage from "../views/premiumSponsorship/PremiumSponsorshipConsentPage.tsx";
import { getPremiumSponsorshipByTokenRequest } from "../services/premiumSponsorship/premiumSponsorshipApi.ts";

const { getLoggedUser } = userService();
const { isLoggedIn } = authService();
const { toastError } = toastsWithIntl(["organization"]);

const logger = loggerBuilder("public-routes");

// eslint-disable-next-line react-refresh/only-export-components
const RedirectRoot = () => {
  // NOTE: Used to reload completely the html HEADERS when on login
  // So that we can load stripe without problem
  // SOURCE: https://github.com/stripe/stripe-js/issues/417
  useLayoutEffect(() => {
    if (isLoggedIn()) {
      window.location.href = "/candidate";
    } else {
      window.location.href = "/login";
    }
  }, []);

  return null;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <RedirectRoot />,
      },
      {
        path: "/contact",
        element: <ContactPage />,
        async loader() {
          return {
            loggedUser: isLoggedIn() ? await getLoggedUser() : null,
          };
        },
      },
      {
        path: "/employeur/:organizationSlug/:capsuleId",
        element: <CapsulePublicPage />,
        async loader({ params }) {
          const loggedUser = await getLoggedUser().catch(() =>
            Promise.resolve(null),
          );
          let capsule: Capsule | null = null;
          let video: Promise<Blob> | null = null;
          try {
            capsule = await getCapsuleByOrganizationSlugAndId(
              params.organizationSlug!,
              Number(params.capsuleId!),
            );
          } catch (e: any) {
            let message: string;
            if (e.response?.status === 404)
              message = "public:capsule.NOT_FOUND";
            else if (e.response?.status === 410)
              message = "public:capsule.DISABLED";
            else message = "public:capsule.GENERIC";
            return {
              loggedUser,
              error: {
                message,
                status: e.response?.status,
              },
            };
          }

          if (capsule?.videoLink) {
            video = (async (videoLink) => {
              const data = await fetchRetriable(videoLink, {
                method: "GET",
                retry: 3,
              });
              return new Blob(
                [new Uint8Array(await data.arrayBuffer()).reverse()],
                {
                  type: "video/mp4",
                },
              );
            })(capsule.videoLink);
          }

          return {
            loggedUser,
            capsule,
            video,
          };
        },
      },
      {
        path: "profile-share/:uid",
        element: <SharedPage />,
        async loader({ params }) {
          let profile: Profile | null = null;
          try {
            profile = await getProfilesByUidRequest(params.uid!);
          } catch (e: any) {
            switch (e.response?.status) {
              case 404:
                toastError("organization:shared-profile.ERROR_NOT_FOUND");
                return redirect("/organization/candidates");
              case 412:
                toastError("organization:shared-profile.ERROR_NOT_AN_ORG");
                return redirect("/candidate");
              case 402:
                toastError(
                  "organization:shared-profile.ERROR_PAYMENT_REQUIRED",
                  {
                    id: "payment-required",
                  },
                );
                return redirect("/organization/candidates");
              case 403:
                toastError(
                  "organization:shared-profile.ERROR_ORG_NOT_VALIDATED",
                  {
                    id: "org-not-validated",
                  },
                );
                return redirect("/organization/candidates");
              case 410:
                toastError(
                  "organization:shared-profile.ERROR_DISABLED_PROFILE",
                  {
                    id: "disabled-profile",
                  },
                );
                return redirect("/organization/candidates");
              case 200:
                break;
              default:
                logger.warn("unknown error", e);
                return redirect("/");
            }
          }

          let video = null;
          if (profile?.videoLink) {
            video = (async (videoLink) => {
              const data = await fetchRetriable(videoLink, {
                method: "GET",
                retry: 3,
              });
              return new Blob(
                [new Uint8Array(await data.arrayBuffer()).reverse()],
                {
                  type: "video/mp4",
                },
              );
            })(profile.videoLink);
          }

          // Pro connecté : la page s'affiche dans la coquille de l'espace pro (menu latéral), qui a besoin de
          // l'utilisateur et de son organisation, comme OrganizationArea. Échec : page seule, comme avant.
          const shell = isLoggedIn()
            ? await getLoggedUser()
                .then(async (loggedUser) =>
                  loggedUser.organizationId
                    ? {
                        loggedUser,
                        organization: await getOrganizationRequest(
                          loggedUser.organizationId,
                        ),
                      }
                    : null,
                )
                .catch(() => null)
            : null;

          return {
            profile,
            video,
            shell,
          };
        },
      },
      {
        path: "premium-sponsorship/:token",
        element: <PremiumSponsorshipConsentPage />,
        async loader({ params }) {
          const sponsorship = await getPremiumSponsorshipByTokenRequest(
            params.token!,
          );
          return { sponsorship };
        },
      },
      authRoutes,
      privateRoutes,
    ],
  },
  {
    path: "embed",
    errorElement: <EmbedErrorPage />,
    loader() {
      if (window.self === window.top) {
        throw new Error("This page can only be accessed in an iframe.");
      }
      return Promise.resolve(null);
    },
    children: [
      {
        path: "profile/:uid",
        element: <ProfileEmbedPage />,
        async loader({ params }) {
          const profile = await getEmbedProfileByUidRequest(String(params.uid));
          const video = (async (videoLink) => {
            const data = await fetchRetriable(videoLink, {
              method: "GET",
              retry: 3,
            });
            return new Blob(
              [new Uint8Array(await data.arrayBuffer()).reverse()],
              {
                type: "video/mp4",
              },
            );
          })(profile.videoLink!);

          return Promise.resolve({
            profile,
            video,
          });
        },
      },
      {
        path: "capsule/:uid",
        element: <CapsuleEmbedPage />,
        async loader({ params }) {
          const capsule = await getCapsuleByUidRequest(String(params.uid));
          const video = (async (videoLink) => {
            const data = await fetchRetriable(videoLink, {
              method: "GET",
              retry: 3,
            });
            return new Blob(
              [new Uint8Array(await data.arrayBuffer()).reverse()],
              {
                type: "video/mp4",
              },
            );
          })(capsule.videoLink!);

          return Promise.resolve({
            capsule,
            video,
          });
        },
      },
    ],
  },
  ...(IS_PROD ? [] : [sxUiRoutes]),
]);

export default router;
